import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getPassengerCities } from "../../../api/client";

export function formatCityLabel(city) {
  if (!city) return "";
  const name = city.nome || city.nomecurto || "";
  const uf = city.uf ? ` — ${city.uf}` : "";
  return `${name}${uf}`.trim();
}

function cityNeedsHydration(city) {
  if (!city?.id) return false;
  const label = formatCityLabel(city);
  if (!label) return true;
  return String(city.nome || "").trim() === String(city.id);
}

async function fetchCityById(id) {
  const raw = await getPassengerCities({ page: 1, limit: 1, id });
  const items = Array.isArray(raw?.items) ? raw.items : [];
  return items[0] ?? null;
}

export default function CityAutocomplete({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  excludeId = null,
}) {
  const { t } = useTranslation("products");
  const [inputValue, setInputValue] = useState(() => formatCityLabel(value));
  const [options, setOptions] = useState(() => (value?.id ? [value] : []));
  const [loading, setLoading] = useState(false);
  const hydratingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const loadCities = useCallback(async (q) => {
    setLoading(true);
    try {
      const raw = await getPassengerCities({ page: 1, limit: 50, q: q || "" });
      const items = Array.isArray(raw?.items) ? raw.items : [];
      setOptions((prev) => {
        const merged = [...items];
        for (const p of prev) {
          if (p?.id && !merged.some((o) => String(o.id) === String(p.id))) merged.push(p);
        }
        if (value?.id && !merged.some((o) => String(o.id) === String(value.id))) {
          merged.unshift(value);
        }
        return merged;
      });
    } catch {
      setOptions((prev) => (value?.id ? [value, ...prev.filter((p) => String(p.id) !== String(value.id))] : []));
    } finally {
      setLoading(false);
    }
  }, [value]);

  useEffect(() => {
    let cancelled = false;

    async function syncValue() {
      if (!value?.id) {
        setInputValue("");
        return;
      }

      if (!cityNeedsHydration(value)) {
        setInputValue(formatCityLabel(value));
        setOptions((prev) =>
          prev.some((o) => String(o.id) === String(value.id)) ? prev : [value, ...prev]
        );
        return;
      }

      if (hydratingRef.current) return;
      hydratingRef.current = true;
      setLoading(true);
      try {
        const city = await fetchCityById(value.id);
        if (cancelled) return;
        if (city) {
          setInputValue(formatCityLabel(city));
          setOptions((prev) =>
            prev.some((o) => String(o.id) === String(city.id)) ? prev : [city, ...prev]
          );
          onChangeRef.current(city);
        } else {
          setInputValue(formatCityLabel(value) || String(value.id));
        }
      } catch {
        if (!cancelled) setInputValue(formatCityLabel(value) || String(value.id));
      } finally {
        if (!cancelled) setLoading(false);
        hydratingRef.current = false;
      }
    }

    syncValue();
    return () => {
      cancelled = true;
    };
  }, [value?.id, value?.nome, value?.uf, value?.nomecurto]);

  useEffect(() => {
    if (cityNeedsHydration(value)) return;
    const tmr = setTimeout(() => loadCities(inputValue.trim()), 300);
    return () => clearTimeout(tmr);
  }, [inputValue, loadCities, value]);

  const filteredOptions = useMemo(() => {
    if (!excludeId) return options;
    return options.filter((o) => String(o.id) !== String(excludeId));
  }, [options, excludeId]);

  return (
    <Autocomplete
      options={filteredOptions}
      value={value}
      onChange={(_e, option) => onChange(option)}
      inputValue={inputValue}
      onInputChange={(_e, v, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(v);
      }}
      getOptionLabel={(o) => formatCityLabel(o)}
      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      noOptionsText={t("trechoForm.noCities")}
      loadingText={t("form.loading")}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={t("trechoForm.citySearchPlaceholder")}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
